import { useCallback, useEffect, useRef, useState } from 'react';

import { sentryLogger } from '@/adapters/logger/sentryLogger';

interface RepositoryState<T> {
  data: T[];
  isLoading: boolean;
  refetch: () => Promise<void>;
}

const areDepsEqual = (left: readonly unknown[], right: readonly unknown[]) => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((value, index) => Object.is(value, right[index]));
};

export const useRepository = <T>(
  fetchFn: () => Promise<T[]>,
  deps: readonly unknown[] = [],
): RepositoryState<T> => {
  const [data, setData] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const fetchFnRef = useRef(fetchFn);
  const depsRef = useRef<readonly unknown[] | null>(null);

  useEffect(() => {
    fetchFnRef.current = fetchFn;
  });

  const refetch = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await fetchFnRef.current();
      setData(result);
    } catch (e) {
      sentryLogger.captureException(e as Error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const previousDeps = depsRef.current;

    const isSame = previousDeps ? areDepsEqual(previousDeps, deps) : false;
    if (isSame) {
      return;
    }

    depsRef.current = deps;
    void refetch();
  });

  return { data, isLoading, refetch };
};
